import { CategoryType, Database, NestedHome, MapHome } from "@habity/types";
import { capitalizeAllWords, capitalizeFirstLetter } from "@habity/utils";
import { formatApartmentNumber } from "./apartmentNumbers";
import { getLivingSpace } from "./livingSpaces";
import { getRooms } from "./rooms";
import { getMonthlyFee } from "./monthlyFees";
import { getPlotSize } from "./plotSizes";
import { formatStreet, getStreet } from "./streets";

export const sanitizeAddress = (
  address: Partial<Database["public"]["Tables"]["addresses"]["Insert"]>
) => {
  const {
    city: inputCity,
    street: inputStreet,
    apartment_number: inputApartmentNumber,
    originalStreetName, // Filter out non-database fields
    ...restAddress
  } = address as any;
  // If no city or street is provided, just return the address
  if (!inputCity && !inputStreet) return restAddress;
  const city = inputCity ? capitalizeAllWords(inputCity, true) : null;
  const street = inputStreet ? formatStreet(inputStreet) : null;
  const apartment_number = formatApartmentNumber(inputApartmentNumber);
  return { ...restAddress, city, street, apartment_number };
};

export const getCategoryText = (category: CategoryType) => {
  const swedishTypes: Record<CategoryType, string> = {
    apartment: "Lägenhet",
    cottage: "Fritidshus",
    farm: "Gård",
    house: "Villa",
    commercial: "Kommersiellt",
    plot: "Tomt",
    other: "Övrigt",
    twinhouse: "Parhus",
    new_construction: "Nyproduktion",
    terraced: "Kedjehus",
    townhouse: "Radhus",
  };
  return swedishTypes[category];
};

export type OwnershipType = Database["public"]["Enums"]["ownership_type"];

export const getOwnershipText = (ownership: OwnershipType): string => {
  const ownershipMap: Record<OwnershipType, string> = {
    Owner: "Äganderätt",
    TenantOwnership: "Bostadsrätt",
    Tenancy: "Hyresrätt",
    SiteLeashold: "Tomträtt",
    Unknown: "Okänd",
  };
  return ownershipMap[ownership] || ownership;
};

export const getCategoryTextPlural = (category: CategoryType) => {
  const swedishTypes: Record<CategoryType, string> = {
    apartment: "lägenheter",
    cottage: "fritidshus",
    farm: "gårdar",
    house: "villor",
    commercial: "kommersiellt",
    plot: "tomter",
    other: "övrigt",
    twinhouse: "parhus",
    new_construction: "Nyproduktion",
    terraced: "kedjehus",
    townhouse: "radhus",
  };
  return swedishTypes[category];
};

export const getCategoryTextDefinite = (category: CategoryType) => {
  const swedishTypes: Record<CategoryType, string> = {
    apartment: "lägenheten",
    cottage: "fritidshuset  ",
    farm: "gården",
    house: "villan",
    commercial: "objektet",
    plot: "tomten",
    other: "övrigt",
    twinhouse: "parhuset",
    new_construction: "Nyproduktionen",
    terraced: "kedjehuset",
    townhouse: "radhuset",
  };
  return swedishTypes[category];
};

export const getHomeSize = (home: NestedHome) => {
  const { rooms, living_space, additional_space } = home.address || {};
  return `${getRooms(rooms)}, ${getLivingSpace(living_space)} boarea${
    additional_space ? `, ${getLivingSpace(additional_space)} biarea` : ""
  }`;
};

export const formatHomeData = (home: NestedHome) => {
  const {
    living_space,
    additional_space,
    plot_size,
    rooms,
    monthly_fee,
    // category,
  } = home.address || {};
  const data: string[] = [];
  // if (category) data.push(getCategoryText(category));
  if (rooms) data.push(getRooms(rooms));
  if (living_space) data.push(getLivingSpace(living_space, additional_space));
  if (plot_size) data.push(getPlotSize(plot_size));
  if (monthly_fee) data.push(getMonthlyFee(monthly_fee));
  return data.join(" • ");
};

export const getFullAddress = (home: NestedHome) => {
  const { street, apartment_number, city, zip } = home.address;
  return `${street}${
    apartment_number ? ` LGH ${apartment_number}` : ""
  }, ${zip} ${city}`;
};

export const getHomeAddress = (home: NestedHome) => {
  const { status, address } = home || {};
  const { street, city } = address || {};
  if (!status.show_address) return getStreet(home);
  return [street, city].join(", ");
};

export const shouldShowAddress = (home: NestedHome | null): boolean => {
  if (!home) return false;
  return home.status?.show_address === true;
};

export const getHomeAttributes = (home: NestedHome) => {
  const { category, living_space, rooms } = home.address || {};
  const attributes = [];
  if (category) attributes.push(getCategoryText(category));
  if (rooms) attributes.push(getRooms(rooms));
  if (living_space) attributes.push(getLivingSpace(living_space));
  return attributes.join(", ");
};

/**
 * Formats location display text with area, municipality, and city
 * Prioritizes area name with municipality, falls back to municipality or city
 * Works with both NestedHome and MapHome address types
 * @param address - Address object with city, municipality, and area
 */
export const getLocationDisplayText = (
  address?:
    | (Pick<
        Database["public"]["Tables"]["addresses"]["Row"],
        "city" | "municipality"
      > & {
        area?: { name?: string } | null;
      })
    | null
): string => {
  if (!address) return "";

  const { city, municipality, area } = address;
  const formattedCity = capitalizeFirstLetter(city || "", true);
  const areaName = area?.name || formattedCity || "";
  const secondPart = municipality || formattedCity || "";

  if (areaName && areaName !== secondPart) {
    return `${areaName}, ${secondPart}`;
  }

  // Build location parts in order: area and municipality
  const parts = [];

  if (areaName) {
    parts.push(areaName);
  }

  if (
    municipality &&
    municipality !== formattedCity &&
    municipality !== areaName
  ) {
    parts.push(municipality);
  }

  // If no parts, fallback to any available value
  if (parts.length === 0) {
    return municipality || formattedCity || "";
  }

  return parts.join(", ");
};
